/*
  @integrate/contracts — the single source of truth for everything that crosses a boundary.

  One zod schema per concept, used in three places:
    - the server validates every request and websocket frame against it,
    - TypeScript types are inferred from it on both sides of the wire,
    - and (from P6) the LLM agent's tool definitions are generated from it.

  That is the whole reason the platform is command-shaped rather than document-shaped: the API,
  the audit log, live editing and the agent's tool surface are all the same list of operations.

  Split three ways as of P3, since the catalogue outgrew a single file:
    domain.ts    — vocabulary and the shapes that travel (people, TSAs, assumptions, views, …)
    commands.ts  — the catalogue: every change the product can make
    index.ts     — the protocol built on top of them, plus the re-exports
*/
import { z } from 'zod';
import { commandSchema } from './commands.js';
import { bucketSchema, filterConditionSchema, groupingLevelSchema, projectRoleSchema, viewGroupingSchema, viewLayoutSchema, viewModeSchema, } from './domain.js';
export * from './domain.js';
export * from './commands.js';
export * from './audio.js';
export * from './people-filter.js';
export * from './scope.js';
/* ------------------------------------------------------------------ *
 * Events — what a command becomes once the server has accepted it
 * ------------------------------------------------------------------ */
export const actorKindSchema = z.enum(['user', 'agent']);
export const eventSchema = z.object({
    id: z.string(),
    /** Monotonic per project. Total ordering, and how a client detects a gap. */
    seq: z.number().int().nonnegative(),
    projectId: z.string(),
    scenarioId: z.string().nullable(),
    type: z.string(),
    actorUserId: z.string(),
    actorName: z.string(),
    actorKind: actorKindSchema,
    command: commandSchema,
    /**
     * The compensating commands, in order, or null when the change is not reversible.
     *
     * A batch rather than a single command because undoing one action is not always one action: putting
     * a person back into Remain has to restore the stranded mitigation plan that joining the carve-out
     * removed. A single-command inverse would silently drop it, which is the failure mode this whole
     * catalogue exists to avoid.
     */
    inverse: z.array(commandSchema).nullable(),
    summary: z.string(),
    /**
     * What the change wrote, as the handlers' own keys — `bucket:E4`, `head:as-is:G:lvl1=Ops`.
     *
     * Recorded on every event since P3 and, until now, read by exactly one thing: the guard that refuses to
     * revert a change somebody has built on top of. It is on the wire because it is the only thing that
     * answers a client's question "did that change what I am looking at" — and a client that re-derived
     * these keys from the command payload would be a SECOND implementation of the handlers' vocabulary,
     * which is the failure `DESTRUCTIVE_INVERSES` was written up about. Receiving what the server recorded
     * is not a second implementation.
     *
     * No new exposure: every key is built from ids the same member already has through `/state` and
     * `/design`, and the `command` payload beside it carries strictly more.
     */
    touched: z.array(z.string()).default([]),
    /** Groups commands applied as one user-visible act, so undo reverses the whole thing. */
    batchId: z.string().nullable().default(null),
    createdAt: z.string(),
});
/**
 * Why a change cannot be reverted.
 *
 * Five answers that one `revertable: false` used to collapse into a single greyed-out row, so that "this
 * can never be undone" and "there is nothing here to undo" read identically.
 *
 *   · `undone` — somebody has already taken it back. Reverting the revert is how it comes back.
 *   · `irreversible` — the handler recorded no inverse, because undoing it would need a command that
 *     deliberately does not exist. A roster refresh is the case: putting the population back would mean
 *     deleting people.
 *   · `nothing-to-undo` — there IS an inverse and it is empty. A note is the case: it changed no state,
 *     and taking it out would be rewriting the history rather than restoring anything.
 *   · `dead-scenario` — the change was made in a scenario that has since been deleted. The event stays in
 *     the log deliberately, because deleting a scenario should not erase the record of what was decided
 *     in it, but there is nothing left for the inverse to name.
 *   · `owner-only` — the reader may not run what the inverse would run. An editor can duplicate a
 *     scenario and the inverse of that is `deleteScenario`, which is the owner's alone, so the reader
 *     needs telling before the click rather than being handed the name of a command they never issued.
 *
 * The last two depend on WHO IS ASKING and on state outside the row, so they are computed per request
 * rather than being properties of the event.
 *
 *   · `stale-destructive` — undoing this would run a command that cannot itself be undone, and it is no
 *     longer the newest change. Three commands invert to something the catalogue declares irreversible,
 *     and each destroys whatever has been built inside what it removes, so the server offers them only
 *     while nothing has happened since. Cheap to answer for a whole page in one query, and exact rather
 *     than approximate, which is the bar: a wrong prediction HIDES a legitimate undo.
 *
 * The last three depend on WHO IS ASKING or on state outside the row, so they are computed per request
 * rather than being properties of the event.
 *
 * A SIXTH refusal is deliberately absent. The supersede guard (apps/server/src/revert.ts) runs only at
 * revert time, because whether somebody has built on a change is a question about every later event and
 * the answer changes under the reader's feet. So an unblocked row means "may be offered", not "will
 * succeed", and that refusal has to land where the click happened.
 */
export const REVERT_BLOCKS = [
    'undone',
    'irreversible',
    /**
     * Everything at or below the newest restore.
     *
     * Added after the P5 exit review found that `assertNotBehindARestore` — the guard P5q added — was the
     * one server refusal with no row-level counterpart, so a restore left every row beneath it holding an
     * Undo whose only possible outcome was a 409. Worth stating precisely, because the review's first
     * framing was looser than the truth: a restore does not ARM new buttons, it removes none of the ones
     * already armed. Same defect P5k closed for one row, across a log.
     *
     * It cannot be predicted from `touched` the way the supersede guard is, because `restoreProject`
     * records `touched: []` deliberately — a restore touches everything and that vocabulary has no
     * wildcard. A seq comparison answers it exactly, and the history query already knows the newest
     * restore.
     */
    'behind-a-restore',
    'nothing-to-undo',
    'dead-scenario',
    'owner-only',
    'stale-destructive',
];
export const revertBlockSchema = z.enum(REVERT_BLOCKS);
/**
 * A note somebody wrote against a change, read back.
 *
 * Its own shape rather than three loose columns because "is there a note" is one question, and because a
 * note has an author who is NOT the event's actor: annotating somebody else's change is the normal case,
 * and a note shown under the actor's name would attribute a colleague's remark to the person it is about.
 */
export const eventNoteSchema = z.object({
    text: z.string(),
    authorName: z.string(),
    /** When it was last written. An annotation is an upsert, so this moves when the note is edited. */
    updatedAt: z.string(),
});
/**
 * A row of the change log: the event, plus the two things a reader needs that the record cannot say.
 *
 * Both are DERIVED at read time, not stored — `undone` is a fact about a LATER event — and both are
 * stated per row so the client does not re-derive the rule and so "why is this greyed out" has one
 * answer rather than a guess.
 *
 * The two overlap and answer different questions: whether the change is in force, and whether a button
 * may be offered. An undone note is both, and `blocked` says `undone` for it, because of the two that is
 * the more useful thing to tell somebody.
 */
export const historyEntrySchema = eventSchema.extend({
    undone: z.boolean(),
    /** Null when a revert may be offered. */
    blocked: revertBlockSchema.nullable(),
    /**
     * How many events share this one's batch, counted over the WHOLE project rather than the page.
     *
     * 1 for a change that was not part of a batch. Counted server-side because a batch can straddle the
     * page boundary: reverting any member reverts all of them, so a row that said "undo all 2 changes"
     * from the two it could see while taking off a third would be a control lying about its own scope.
     */
    batchSize: z.number().int().positive(),
    /**
     * The change that came after this one and takes its destructive undo away, when there is one.
     *
     * Carried so the row can NAME it — "#412 came after it" — rather than saying a change happened at some
     * point. Always on the page when the row is, since it is by definition later and the log is newest
     * first, so the client can look up what it was. Null whenever `blocked` is not `stale-destructive`.
     */
    staleAfterSeq: z.number().int().nonnegative().nullable().default(null),
    /**
     * The newest restore later than this change, when there is one.
     *
     * A SECOND seq field rather than a reuse of `staleAfterSeq`, because the two name different things and
     * a single "the seq that blocked this" would have to be resolved against the precedence order on the
     * way out — a rule the server would then hold twice. They are also independent: a change can be both
     * behind a restore and superseded by a later destructive change, and each sentence wants its own.
     *
     * A FACT about the row, not a restatement of `blocked`: it is filled whenever a restore came after,
     * including on rows refused for a higher-precedence reason, the same way `staleAfterSeq` is. That
     * matters because the two are computed from the log and `blocked` is computed from them.
     */
    restoredAtSeq: z.number().int().nonnegative().nullable().default(null),
    /**
     * Who took this change back, and at which change — when somebody has.
     *
     * `undone` is the one refusal that is BY DEFINITION about another person's action, and it was the only
     * one naming neither a person nor a seq: "Already undone. Undo the revert further up to put it back."
     * The link existed all along — `change_events.undone_by` holds the reverting event's id — but it was
     * reduced to a boolean on the way out and no field carried the rest.
     *
     * "Further up" is also not dependable advice. With "Only my changes" ticked, the revert this sentence
     * tells the reader to go and undo is filtered out of the list they are looking at, so the instruction
     * points at a row that is not there. Naming the person and the number is what survives the filter.
     */
    undoneBy: z
        .object({ seq: z.number().int().nonnegative(), actorName: z.string() })
        .nullable()
        .default(null),
    /**
     * The note against this change, or null.
     *
     * Carried on the history row rather than fetched separately because `annotateEvent` was write-only:
     * the command stored a note and no endpoint returned one, so the only way to read back what somebody
     * had written was to query the database. A projection with no reader is a feature that does not exist.
     */
    note: eventNoteSchema.nullable().default(null),
});
/**
 * One point the project can be restored to.
 *
 * A LIST of recorded points rather than an open sequence range, because that is what the storage can
 * honestly offer: points are written immediately before every change that cannot be undone, plus a
 * cadence. `reason` is what makes the list readable — one taken before a roster refresh is a rescue, one
 * from the cadence is where the clock landed — and it is a plain string rather than an enum so a new
 * reason reaches the reader rather than failing validation on the way.
 */
export const restorePointSchema = z.object({
    seq: z.number().int().nonnegative(),
    reason: z.string(),
    createdAt: z.string(),
    /** The change this point sits immediately before, when there is one. Null at the origin and the tip. */
    nextSummary: z.string().nullable(),
});
/** What a revert answers with: the events it appended, and the seqs now marked undone. */
export const revertResultSchema = z.object({
    events: z.array(eventSchema),
    reverted: z.array(z.number().int().nonnegative()),
});
/* ------------------------------------------------------------------ *
 * Exports — the two request shapes, so one declaration validates both sides
 * ------------------------------------------------------------------ */
/*
  The deck dialog is five checkboxes, two selects and a depth, and every one of them is a REQUEST field
  rather than remembered state.

  The app re-derives its defaults from the data every time the dialog opens (orgchart.html:10819) rather
  than making them sticky, so a deck exported from a project with no stage data never silently carries a
  previous project's choices. `suggestedDeckOptions` in @integrate/core is that derivation, and it lives
  there rather than in @integrate/export because the client has to run it to fill the dialog in and
  cannot depend on a package carrying a spreadsheet writer and a PowerPoint writer.

  `dateLabel` is a field and not a server-side `new Date()` on purpose: the original called
  `toLocaleDateString()`, which on a server is the SERVER's locale and timezone rather than the reader's.
*/
export const deckDensitySchema = z.enum(['detailed', 'balanced', 'compact']);
export const deckMetricSchema = z.enum(['hc', 'fte', 'cost']);
export const deckOptionsSchema = z.object({
    /** Which scenario the deck is drawn from. The active one when absent. */
    scenarioId: z.string().min(1).optional(),
    /** How deep the trees are drawn. The app reads this off the live chart; a server has no chart. */
    depth: z.number().int().min(1).max(8).default(3),
    density: deckDensitySchema.default('balanced'),
    /** One carve-out chapter per separation stage instead of a single one. */
    stages: z.boolean().default(false),
    /** The target-org chapter. Ignored unless the project has a target design. */
    target: z.boolean().default(true),
    summary: z.boolean().default(true),
    bridge: z.boolean().default(true),
    bridgeMetric: deckMetricSchema.default('hc'),
    stranded: z.boolean().default(true),
    /**
     * The three late-arriving chapters default OFF at the schema, unlike summary/bridge/stranded:
     * a bare API call keeps producing the deck it always produced, and the dialog turns each on
     * when the project has the data (`suggestedDeckOptions`, and the scenario count for compare).
     */
    tsas: z.boolean().default(false),
    oneoff: z.boolean().default(false),
    compare: z.boolean().default(false),
    /** Already formatted by the caller, whose locale it is. */
    date: z.string().max(60).optional(),
});
export const workbookOptionsSchema = z.object({
    scenarioId: z.string().min(1).optional(),
    /** Omit the hidden metadata sheet for a data-only handover. */
    includeMeta: z.boolean().default(true),
    /**
     * Narrow the export the way the caller has narrowed their screen.
     *
     * Opt-in, and unfiltered by default: the shipped app exports through whatever filters happen to be
     * applied, which on a shared server would mean one person's narrowing shaping somebody else's
     * handover file — and a scheduled export has no screen at all.
     */
    filters: z.enum(['none', 'current']).default('none'),
});
/**
 * The buyer pack takes almost nothing on purpose: every knob a caller could turn is a way to
 * produce a variant the seller's counsel has not reviewed. One deliverable, one shape.
 */
export const buyerPackOptionsSchema = z.object({
    scenarioId: z.string().min(1).optional(),
});
/**
 * What issuing a SteerCo pack takes: the deck's own options, plus the pack's name.
 *
 * The deck options ride along unchanged because the pack's deck IS the deck — one dialog state, one
 * schema, no second vocabulary to drift. `name` is what the committee calls the pack; it becomes the
 * event summary and, through `nextSummary`, the restore point's label.
 */
export const steercoPackOptionsSchema = deckOptionsSchema.extend({
    name: z.string().trim().min(1).max(200),
});
/**
 * One scenario priced for the comparison pane — the deck's compare slide served as data.
 *
 * The row set and the slide's are the same on purpose: what the SteerCo reads on paper and what the
 * team reads on screen must be one computation (`computeStageBridge`, `computeOneOffCosts`,
 * `computeStrandedTimeline`, per design, over one roster). The wire carries NUMBERS rather than
 * formatted strings because the client owns the locale — and both bridge weights, so the pane's
 * hc/fte toggle is a re-render rather than a refetch.
 */
export const scenarioCompareRowSchema = z.object({
    id: z.string(),
    name: z.string(),
    isDefault: z.boolean(),
    /** The governance gate, on the row: which options are frozen is part of comparing them. */
    perimeterFrozen: z.boolean(),
    hc: z.object({ day1: z.number(), day2: z.number() }),
    fte: z.object({ day1: z.number(), day2: z.number() }),
    /** Day-1 carve-out run-rate in RAW annual salary — the weight every cost bridge in the app uses. */
    costDay1: z.number(),
    /** Severance + hiring + retention: the whole one-off envelope. */
    oneOffTotal: z.number(),
    /** RemainCo run-rate after mitigation (loaded), per annum. */
    strandedResidual: z.number(),
});
export const scenarioCompareResponseSchema = z.object({
    rows: z.array(scenarioCompareRowSchema),
});
/* ------------------------------------------------------------------ *
 * The scheduled design review
 * ------------------------------------------------------------------ */
/**
 * One finding as a run stores and serves it.
 *
 * THE TITLE AND NOT THE DETAIL, which is the whole disclosure decision made once: `reviewFindings`
 * guarantees a title is a count and a label while a detail names people, so what a run persists —
 * and therefore what any later reader of that run can be shown — carries no one's name. Stored
 * artefacts are the one thing this product never re-narrows for a second reader; this is the shape
 * that needs no narrowing.
 */
export const reviewFindingSchema = z.object({
    /** The check's stable id, which is what lets two runs be compared at all. */
    id: z.string(),
    severity: z.enum(['misstated', 'undecided']),
    n: z.number(),
    title: z.string(),
});
/**
 * A finding that appeared or went, carrying the sentence from the run that could see it.
 *
 * THE TITLE TRAVELS WITH THE ID because a RESOLVED finding is, by definition, absent from today's
 * review — there is no live sentence to look it up in, and rendering the bare check id showed a
 * reader `tsa-uncovered` in a slug. It comes from the previous run, which is the last thing that
 * could say it in words.
 */
export const reviewMovementSchema = z.object({ id: z.string(), title: z.string() });
export const analystRunSchema = z.object({
    ranAt: z.string(),
    /** The project's change number this review read — what makes two runs comparable. */
    seq: z.number(),
    /** WHICH DESIGN was reviewed: a run is a review of a scenario, not of a project. */
    scenarioId: z.string(),
    scenarioName: z.string(),
    findings: z.array(reviewFindingSchema),
    /** Findings this run reports and the previous review of the same scenario did not. */
    added: z.array(reviewMovementSchema),
    /** Findings that review reported and this one does not. */
    resolved: z.array(reviewMovementSchema),
    /** When the run this was compared against happened, or null when this is the first. */
    comparedTo: z.string().nullable(),
});
/** `null` when nobody has ever reviewed this project — a different answer from "nothing found". */
export const reviewResponseSchema = z.object({ run: analystRunSchema.nullable() });
/* ------------------------------------------------------------------ *
 * Plan versus what actually happened
 * ------------------------------------------------------------------ */
/**
 * One departure, beside the plan the refresh cleared when it recorded the departure.
 *
 * NO MONEY, anywhere: months, actions and a name. That is why this payload has no salary gate while
 * the scenario comparison does — there is nothing here a withheld grant could be told wrongly.
 */
export const departureRowSchema = z.object({
    employeeId: z.string(),
    /**
     * Null when this employee id is not in the roster the reader read. The row still counts.
     *
     * TWO CAUSES, AND ONLY ONE IS A PERMISSION — which is why this says "the roster the reader read"
     * rather than "the reader may not see them". Either their grant did not admit the person, or the
     * person is not in the roster at all: the ledger is append-only and survives a restore, so a
     * departure outlives the employee row a rollback removed. A reader told the second case was the
     * first would be told something false about their own access.
     */
    name: z.string().nullable(),
    /** When the departure was recorded, as an ISO instant. */
    at: z.string(),
    /** The project's change number it landed at. */
    seq: z.number(),
    plannedAction: z.enum(['rightsize', 'redeploy']).nullable(),
    plannedMonth: z.number().nullable(),
    plannedDay2Exit: z.boolean(),
    /**
     * The month after Day 1 the departure fell in, or null when the project has no Day-1 date.
     *
     * NULL IS NOT ZERO. It means the question has no answer yet, not that the answer is "at the
     * close" — and rendering it as a month would date every departure to Day 1.
     */
    actualMonth: z.number().nullable(),
    /** actual − planned, in months. Negative is early. Null whenever either side is. */
    varianceMonths: z.number().nullable(),
});
export const varianceSchema = z.object({
    rows: z.array(departureRowSchema),
    /** How many of these departures carried a mitigation plan. */
    planned: z.number(),
    early: z.number(),
    late: z.number(),
    /**
     * Whether the project has stated a Day-1 date, and therefore whether any variance is computable.
     *
     * Carried rather than inferred from the rows: an empty variance column and a project where
     * everything went to plan look identical, and they are opposite claims.
     */
    onACalendar: z.boolean(),
});
export const varianceResponseSchema = z.object({ variance: varianceSchema });
/* ------------------------------------------------------------------ *
 * Realtime protocol
 * ------------------------------------------------------------------ */
export const presenceMemberSchema = z.object({
    userId: z.string(),
    name: z.string(),
    /** Distinguishes two tabs belonging to the same person. */
    connectionId: z.string(),
    selectedId: z.string().nullable(),
    scenarioId: z.string().nullable(),
});
export const clientMessageSchema = z.discriminatedUnion('t', [
    /** First frame. lastSeq lets the server replay only what this client missed. */
    z.object({ t: z.literal('hello'), lastSeq: z.number().int().nonnegative().default(0) }),
    /** cid correlates the optimistic local apply with its ack or reject. */
    z.object({ t: z.literal('cmd'), cid: z.string().min(1), command: commandSchema }),
    z.object({
        t: z.literal('presence'),
        selectedId: z.string().nullable().default(null),
        scenarioId: z.string().nullable().default(null),
    }),
    z.object({ t: z.literal('ping') }),
]);
export const REJECT_CODES = ['forbidden', 'invalid', 'conflict', 'not_found', 'internal'];
export const rejectCodeSchema = z.enum(REJECT_CODES);
/**
 * Why a socket was thrown out of a project it was in.
 *
 * The two ways access ends while somebody is sitting in the project, and they are different enough that
 * one word cannot serve both: a deleted project is gone for everybody and there is nothing to go back to,
 * while removed access means the project is still there and somebody else can grant it again. A client
 * that only knew "you were evicted" would have to guess which sentence to show.
 *
 * `signed-out` is the third and it is about the READER rather than the project: they still have access,
 * the project is untouched, and the reason their socket is going is that this account signed out. A
 * websocket authenticates once at the upgrade and nothing revalidates afterwards, so without this an
 * already-open tab kept receiving events and committing commands as the logged-out user.
 *
 * Sent as a FRAME rather than left to the close code, because a websocket close code is not a reliable
 * channel: `realtime.test.ts` already accepts 1006 alongside 1008 for the upgrade refusals, since an
 * abnormal close is what a client sees when the handshake is cut. A frame arrives before the close and
 * says the thing exactly.
 */
export const EVICT_REASONS = [
    'project-deleted',
    'access-removed',
    'signed-out',
    /*
      The account itself was switched off, rather than access to one project being withdrawn.
  
      Distinct from 'signed-out' because the two are opposite instructions: signing out tells you to
      sign in again, and this one tells you that will not work. Distinct from 'access-removed' because
      that is about a project and this is about you.
    */
    'account-deactivated',
    /*
      The member's SCOPE changed — what they may see, not whether they may be here.
  
      Its own reason because the instruction is different from all four above: sign in again, you
      cannot sign in, the project is gone, you were removed. This one is "reconnect, and you will see
      something different" — the socket cached which people this member may read about at the upgrade
      and cannot refresh it, so the honest move is to end the connection rather than let a stale view
      keep answering.
    */
    'access-changed',
];
export const evictReasonSchema = z.enum(EVICT_REASONS);
export const serverMessageSchema = z.discriminatedUnion('t', [
    z.object({
        t: z.literal('welcome'),
        connectionId: z.string(),
        seq: z.number().int().nonnegative(),
        members: z.array(presenceMemberSchema),
        /** Set when the client is too far behind to catch up by replay and must refetch state. */
        resyncRequired: z.boolean().default(false),
    }),
    z.object({ t: z.literal('event'), event: eventSchema }),
    /*
      A SEQUENCE NUMBER WITH NOTHING IN IT: something happened, and none of it was yours.
  
      NOT OPTIONAL, and this is the subtle part of scoping a live feed. The client tracks `lastSeq` and
      the hello handler computes how far behind it is, resyncing past a threshold — so a scoped member
      who was simply not SENT the events about other people would have a watermark that never advanced,
      and would drift into a forced full refetch on every reconnect. A bare seq advances it and carries
      no content at all: no summary, no command, no ids.
  
      It says nothing about WHAT changed, deliberately. "Somebody edited a person you cannot see" is
      already more than a scope should admit, and a client that rendered it would be putting a notice on
      screen about a population it was narrowed to exclude.
    */
    z.object({ t: z.literal('skip'), seq: z.number().int().nonnegative() }),
    /*
      YOUR SLICE CHANGED AND THE FRAME CANNOT BE SENT: refetch.
  
      For an event that straddles the boundary — "Moved 300 people to Carve-out", forty of them yours.
      The member genuinely needs to know something changed and must not be told what the sentence says,
      and `summary` is prose the handlers wrote, so there is no honest way to trim it. Rewriting free
      text to remove names is unbounded and would be wrong the first time an employee shares a name with
      a business unit.
  
      It carries the seq too, so a client that refetches still knows where it is.
    */
    z.object({ t: z.literal('stale'), seq: z.number().int().nonnegative() }),
    /*
      The ack carries the EVENT, not just its number.
  
      The hub publishes with the originating connection excluded — an author applied their own change
      optimistically and must not have it applied twice, which `realtime.test.ts` asserts — so this frame is
      the only thing the author ever gets back. It used to carry `{cid, seq}` alone, and every consumer that
      wanted more had to do without: the Activity ticker is fed from events, so a bucket move made over the
      socket, the single most frequent action in the product, never appeared in its own author's feed while
      every other path (HTTP fallback, dispatch, revert, note, a colleague's change) did.
  
      Carrying it here rather than also broadcasting it back keeps the no-echo invariant exactly as it is:
      this is an `ack`, it is not run through `applyRemoteEvent`, and nothing about the state it describes
      is applied a second time. `seq` stays alongside for the callers that only want the number.
    */
    z.object({
        t: z.literal('ack'),
        cid: z.string(),
        seq: z.number().int().nonnegative(),
        event: eventSchema,
    }),
    z.object({ t: z.literal('reject'), cid: z.string(), code: rejectCodeSchema, message: z.string() }),
    z.object({ t: z.literal('presence'), members: z.array(presenceMemberSchema) }),
    z.object({ t: z.literal('pong') }),
    /**
     * The last frame this socket will get: you are out, and here is why.
     *
     * Carries a `message` as well as a `reason` for the same reason `reject` does — the code is what the
     * client branches on, the sentence is what a person reads, and having the server author it keeps one
     * copy of the wording rather than one per client.
     */
    z.object({ t: z.literal('evicted'), reason: evictReasonSchema, message: z.string() }),
]);
/* ------------------------------------------------------------------ *
 * HTTP API
 * ------------------------------------------------------------------ */
export const loginRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
export const userSchema = z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    isAdmin: z.boolean(),
});
/**
 * An account as an administrator sees it.
 *
 * `User` is who YOU are — it rides on the session and every request carries it. This is a row in a
 * list of other people, so it carries the two things that are nobody's business but an
 * administrator's: whether the account can still be used, and when it was made. It deliberately
 * does not carry `passwordHash` or `oidcSub`; neither is any use on screen and both are the kind of
 * field that leaks by being present.
 */
export const adminUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    isAdmin: z.boolean(),
    isActive: z.boolean(),
    createdAt: z.union([z.string(), z.date()]),
});
/** What an administrator may change about somebody else. Email is not editable: it is the identity. */
export const adminUserPatchSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    isAdmin: z.boolean().optional(),
    isActive: z.boolean().optional(),
})
    .refine((p) => Object.keys(p).length > 0, { message: 'Nothing to change' });
/**
 * What you may change about YOUR OWN account — which is your name, and nothing else.
 *
 * There was no such route at all. `adminUserPatchSchema` has accepted `name` since P8b and
 * `AccountModal` rendered no control for it, so an administrator could not fix a misspelled name;
 * nobody could change their own at all, and a name is what every other person in the product sees on
 * the presence strip, in every change-log row and on every proposal.
 *
 * STRICT, unlike the administrator's patch above, and the difference is deliberate. `isAdmin` and
 * `isActive` are powers, and a caller who sends them here is asking to promote themselves — silently
 * stripping the keys would answer 200 to that request. So would silently stripping `email`, which is
 * the identity and is not editable by anybody. An unknown key is refused and named rather than
 * dropped, because a client that believes it changed something it did not is the worse failure.
 */
export const ownProfilePatchSchema = z.object({ name: z.string().min(1).max(200) }).strict();
/**
 * The floor for any password this product stores.
 *
 * One constant because it is stated in three places that must agree — the create route, the
 * bootstrap administrator, and now both password-setting routes. They had drifted apart once
 * already by being written out separately.
 */
export const MIN_PASSWORD_LENGTH = 12;
export const passwordSchema = z.string().min(MIN_PASSWORD_LENGTH).max(200);
export const projectSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    dealType: z.enum(['separation', 'integration']),
    role: projectRoleSchema,
    employeeCount: z.number().int().nonnegative(),
    updatedAt: z.string(),
    /**
     * Archived projects are read-only and hidden from the list unless asked for.
     *
     * `listForUser` has returned this since archiving was built and the schema did not declare it, so
     * the one field that says a project cannot be edited was travelling undeclared — a client reading
     * it would have been reading a value no contract promised, and a client validating the payload
     * would have stripped it.
     */
    archived: z.boolean(),
});
/**
 * One person's access to one project.
 *
 * The name and the email both, because they answer different questions on the same row: the name is who
 * this is, and the email is which account — and an engagement with two people called the same thing is
 * ordinary. The email is also the identifier the grant route takes, so showing it is what makes the list
 * and the form agree about who is who.
 *
 * Not `User`: that carries `isAdmin`, which is a platform-wide fact and none of a project's business.
 */
export const projectMemberSchema = z.object({
    userId: z.string(),
    name: z.string(),
    email: z.string(),
    role: projectRoleSchema,
    /**
     * The two access axes, so the dialog can show what somebody was actually given.
     *
     * READABLE BY EVERY MEMBER, like the role beside it and for the same reason `MembersEditor` gives
     * about the list itself: a reviewer already sees these names on the presence strip, and knowing
     * that a colleague's view is narrowed is what stops two people comparing totals and concluding one
     * of them is broken. It is a fact about ACCESS, not about the population — the scope names a filter
     * and never a count.
     */
    scope: z.record(z.string(), z.unknown()).nullable().default(null),
    salaryHidden: z.boolean().default(false),
});
/**
 * One person's access to one project, across the whole deployment — the administrator's view.
 *
 * THE QUESTION NOTHING COULD ANSWER. `AccountModal` lists accounts and `MembersEditor` lists one
 * project's members; no route joined the two, so "what does this consultant still reach" had to be
 * answered by opening every project in turn — and only the projects the person asking is themselves
 * a member of, which is not all of them.
 *
 * FLAT ROWS rather than a nested shape, because the reader groups it both ways: by person when
 * somebody rolls off an engagement, and by project when an owner has left. A nested payload picks
 * one of those and makes the other a client-side regroup of a shape that was already regrouped.
 *
 * `archived` travels because an archived project is read-only, so access to one is a materially
 * different fact from access to a live one and a list that flattened them would overstate the reach.
 */
export const accessRowSchema = z.object({
    userId: z.string(),
    userName: z.string(),
    userEmail: z.string(),
    /** A deactivated account still holds its memberships; deactivation is not a revocation. */
    isActive: z.boolean(),
    projectId: z.string(),
    projectName: z.string(),
    role: projectRoleSchema,
    archived: z.boolean(),
});
/**
 * The kinds of thing that happen to somebody's access.
 *
 * A CLOSED LIST IN CONTRACTS rather than free text on the row, for the reason every command type is:
 * the writer and the reader have to agree, and a typo in a string literal is a row that renders as
 * nothing and is found by nobody. The database deliberately carries no check constraint — the
 * vocabulary is validated here, at the door, where a bad value produces a message somebody can read.
 *
 * `member.removed` and `member.left` are the same route and two different acts, so they are two
 * kinds: "an owner withdrew this" and "they gave it up" are what somebody reading the log needs to
 * tell apart, and reconstructing it by comparing actor to subject would be re-deriving at read time
 * a fact the writer knew.
 */
export const accessEventKindSchema = z.enum([
    'user.created',
    'user.deactivated',
    'user.reactivated',
    'user.admin-granted',
    'user.admin-revoked',
    'user.renamed',
    'user.password-reset',
    'member.granted',
    'member.role-changed',
    'member.removed',
    'member.left',
    /*
      The first READ events in a vocabulary that was all identity lifecycle until now, and the reason
      they are events at all: an export is the one read that LEAVES the deployment. Every salary in the
      project walks out in a file that no scope, socket or session can reach after the response is sent,
      so "who downloaded the workbook, and when" is the first question after any leak — and until these
      two kinds existed, the honest answer was "nothing recorded it".
  
      Actor and subject are the same person on both: the act is somebody exercising their own access,
      not something done TO somebody. `detail` carries the scenario exported and whether the grant was
      scoped, because a scoped export is a slice and a full one is the roster, and six months later the
      difference is the whole answer.
    */
    'export.workbook',
    'export.deck',
    /** The counterparty-facing pack: aggregated and pseudonymised, but still a file that leaves. */
    'export.buyer',
    /** The SteerCo pack: deck + workbook rendered as one act from one seq, which the detail names. */
    'export.steerco',
]);
/**
 * One row of the access log, with the names joined on for reading.
 *
 * EVERY NAME IS NULLABLE AND EVERY ID IS NOT. `bootstrapAdmin` acts before there is an actor; a
 * project can be deleted while the record of who reached it survives, which is the whole reason
 * there is no foreign key. Rendering "on a project that has since been deleted" needs the null to be
 * representable rather than papered over.
 */
export const accessEventSchema = z.object({
    id: z.string(),
    at: z.string(),
    kind: accessEventKindSchema,
    actorUserId: z.string().nullable(),
    actorName: z.string().nullable(),
    subjectUserId: z.string().nullable(),
    subjectName: z.string().nullable(),
    projectId: z.string().nullable(),
    projectName: z.string().nullable(),
    /** What changed — both roles on a role change, nothing but its occurrence on a reset. */
    detail: z.record(z.string(), z.unknown()),
});
/**
 * A change somebody suggested but may not make — the reviewer's and the agent's way through.
 *
 * Here rather than in `apps/server/src/proposals.ts`, where these shapes were declared as bare interfaces:
 * a proposal crosses the boundary the moment a client can read one, and this file exists so there is one
 * declaration of what crossed it. The service kept its own copy for as long as nothing on the other side
 * had ever asked.
 *
 * `createdByName` beside `createdBy`, and `reviewedByName` beside `reviewedBy`. The ids alone are what the
 * service returned, so a proposals list could say "proposed by u_3f8a…" and nothing else — the same defect
 * P5ak fixed three times over in the history modal, in the one place where naming the person is not a
 * courtesy but the point: a proposal is a request made TO somebody BY somebody.
 *
 * Nullable names, left-joined, for the reason `eventsSince` gives about its own actor join: a proposal
 * outlives the account that raised it, and a list that drops rows when somebody leaves the firm is not a
 * record of what was suggested.
 */
export const proposalStatusSchema = z.enum(['open', 'applied', 'rejected', 'withdrawn']);
export const proposalSummarySchema = z.object({
    id: z.string(),
    projectId: z.string(),
    scenarioId: z.string().nullable(),
    title: z.string(),
    status: proposalStatusSchema,
    commandCount: z.number().int().nonnegative(),
    createdBy: z.string(),
    createdByName: z.string().nullable(),
    /** `agent` is not decoration: it is how the audit trail says a machine suggested this. */
    actorKind: actorKindSchema,
    createdAt: z.string(),
    reviewedBy: z.string().nullable(),
    reviewedByName: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    reviewNote: z.string().nullable(),
    appliedBatchId: z.string().nullable(),
});
export const proposalDetailSchema = proposalSummarySchema.extend({
    commands: z.array(commandSchema),
    /** What the proposer was reading, or an agent's reasoning. Kept for the record, never executed. */
    transcript: z.string().nullable(),
});
/** A call the model made that did not survive validation, named rather than dropped. */
export const rejectedCallSchema = z.object({ name: z.string(), reason: z.string() });
/**
 * One question and its answer.
 *
 * ONE SHAPE FOR TWO JOBS, deliberately: this is both what comes back from an ask and what is in the
 * stored thread, because they are the same thing and two types would drift the moment one gained a
 * field. The server's `AgentTurn` was the first of them and is now this.
 *
 * ALL THREE OUTCOMES ARE REPRESENTABLE, which is the reason none of these fields is required:
 *
 *  · a PROPOSAL, when the model called tools — `proposal` set, `answerText` usually null.
 *  · WORDS, when it did not — `answerText` set, `proposal` null. A question the command catalogue
 *    cannot answer is a legitimate answer, not a failure.
 *  · NEITHER, when it called nothing and said nothing worth keeping. A row with both null is "the
 *    assistant had nothing to suggest for that", and it belongs in the thread like any other turn.
 */
export const agentTurnSchema = z.object({
    id: z.string(),
    /** Whole, not truncated. `proposals.title` holds the first 120 characters; this holds the sentence. */
    prompt: z.string(),
    /**
     * `text`, not `answerText`, because that is what the wire has always called it and two tests assert
     * the shape by exact match. The COLUMN is `answer_text`, which is right for a schema and wrong for a
     * field somebody reads beside `prompt`.
     */
    text: z.string().nullable(),
    proposal: proposalSummarySchema.nullable(),
    rejected: z.array(rejectedCallSchema),
    /** False when the roster was too large to describe and the model saw only the org's shape. */
    sawRoster: z.boolean(),
    /**
     * How the turn arrived.
     *
     * A SPOKEN PROMPT IS A TRANSCRIPTION, not a sentence somebody wrote — it can be wrong in ways a
     * typed one cannot, and a reader scrolling back needs to know which they are looking at before
     * they decide whether they meant it.
     */
    via: z.enum(['text', 'voice']),
    /**
     * The scenario it was asked in, which the transcript needs for two different jobs.
     *
     * On screen it marks a turn asked somewhere else. In the feed to the model it is the FILTER — see
     * `agent.ts` — because `propose.ts` stamps every scenario-scoped command with the CURRENT turn's
     * scenario and never offers the model the field.
     */
    scenarioId: z.string().nullable(),
    createdAt: z.string(),
});
export const employeeSchema = z.object({
    id: z.string(),
    name: z.string(),
    supervisorId: z.string().nullable(),
    jobTitle: z.string().nullable(),
    location: z.string().nullable(),
    country: z.string().nullable(),
    /** The currency the salary is in. Conversion happens at read time via the fxRates assumption. */
    currency: z.string().nullable(),
    /** Pension scheme membership — a deal-risk tripwire, never priced here. */
    pensionScheme: z.string().nullable(),
    /** Seniority/grade. Resolves severance months ahead of country. */
    seniority: z.string().nullable(),
    businessUnit: z.string().nullable(),
    legalEntity: z.string().nullable(),
    salary: z.number().nullable(),
    fte: z.number().nullable(),
    dedication: z.number().nullable(),
    openPosition: z.boolean(),
    inactive: z.boolean(),
    /** Hierarchy level values, keyed lvl1..lvlN. Ragged: a blank value collapses the level. */
    levels: z.record(z.string(), z.string().nullable()),
});
export const scenarioSchema = z.object({
    id: z.string(),
    name: z.string(),
    isDefault: z.boolean(),
    /**
     * The governance gate: while true, this scenario's perimeter-deciding commands are refused as
     * direct edits and land only as applied proposals. Travels with the scenario list for the same
     * reason `archived` travels with the project — the one state that changes what the surface does
     * must be visible on the surface, not discovered one refusal at a time.
     */
    perimeterFrozen: z.boolean(),
});
/** Everything a client needs to render a project. The `seq` pins it to a point in the event log. */
export const projectStateSchema = z.object({
    projectId: z.string(),
    name: z.string(),
    dealType: z.enum(['separation', 'integration']),
    seq: z.number().int().nonnegative(),
    groupingLevels: z.array(groupingLevelSchema),
    employees: z.array(employeeSchema),
    scenarios: z.array(scenarioSchema),
    activeScenarioId: z.string(),
    /** employeeId -> bucket, for the active scenario. Absent means the default. */
    decisions: z.record(z.string(), bucketSchema),
    role: projectRoleSchema,
    /**
     * Whether this project is archived, and therefore refuses every write.
     *
     * The client had no way to know. `commands.ts` refuses everything but `archiveProject` on an
     * archived project, so an owner who archived one and stayed on the screen got a generic refusal
     * for every subsequent click with nothing anywhere saying why — the one state that changes what
     * the whole surface does was visible only in the project LIST, which you have already left.
     */
    archived: z.boolean(),
});
/**
 * One command, or a batch applied as a unit.
 *
 * A union rather than always-an-array because the single-command case is overwhelmingly the common one
 * and should not have to dress itself up. A batch exists for the two cases that genuinely are one act:
 * applying an inverse (which may be several commands) and applying a reviewed proposal.
 */
export const commandRequestSchema = z.union([
    z.object({ command: commandSchema }),
    z.object({ commands: z.array(commandSchema).min(1).max(200) }),
]);
export const commandResponseSchema = z.object({
    /** Present when a single command was sent, for callers that expect exactly one. */
    event: eventSchema.optional(),
    events: z.array(eventSchema),
});
/* ------------------------------------------------------------------ *
 * Per-user preferences — deliberately not commands
 * ------------------------------------------------------------------ */
/**
 * How one person is looking at a project.
 *
 * Its own endpoint rather than a command, and that is the whole point: a preference emits no event,
 * advances no sequence number, broadcasts to nobody and appears in no history. Replaying one user's
 * filter onto everybody else's screen is the quickest way to make a shared project unusable, and an
 * audit trail full of "narrowed to Germany" is an audit trail nobody reads.
 *
 * Four fields, and what they have in common is that each is a way of READING the design rather than a
 * change to it. The shared DEFINITION of a bundle of them is a saved view, which is project state
 * reached by a command — a view is something you publish; which view you are in right now is yours.
 *
 * Phase, stage, perspective and target view are held client-side and reset on open by design: a phase
 * is where you are working this minute, not a setting. Filters, the layout and a pivot are not — a
 * six-level pivot is work, and losing it on reload is the annoyance this row exists to remove.
 */
export const projectPrefsSchema = z.object({
    filters: z.array(filterConditionSchema).max(20),
    /** 'hide' removes out-of-scope people; 'grey' keeps them visible but dimmed. */
    viewMode: viewModeSchema,
    viewLayout: viewLayoutSchema,
    /** A custom pivot replacing the canonical hierarchy, for viewing only. */
    viewGrouping: viewGroupingSchema,
    /**
     * Which scenario THIS reader has open. Null means "whatever the project opens on".
     *
     * Per user, not per project, and `user_project_prefs.active_scenario_id` has carried the comment
     * "Two people may legitimately work in different scenarios at the same time" since migration 0003 —
     * while being absent from this schema, from `loadPrefs` and from `savePrefs`, so nothing could set it
     * and every client asked for the project default. Comparing two carve-out options is inherently a
     * two-person activity on a shared project; with one answer per project it was two people taking turns.
     *
     * Nullable rather than defaulted to the project's own default, because "I have not chosen" and "I chose
     * the one that happens to be the default" differ the moment somebody moves the default: the first
     * follows, the second does not.
     */
    activeScenarioId: z.string().nullable(),
});
/** A PATCH sends only what changed; an empty body is a valid no-op. */
export const projectPrefsPatchSchema = projectPrefsSchema.partial();
/* ------------------------------------------------------------------ *
 * A spoken session, between the browser and OUR server
 * ------------------------------------------------------------------ */
/*
  TWO SOCKETS, NOT ONE, AND THIS IS THE NEAR ONE. The browser talks to us; we talk to Azure. That
  extra hop is the whole security design and it is the same argument `transcribe.ts` makes and calls
  "not negotiable": the API key stays on the server, the redaction gate stays on the server, and the
  tool calls that become proposals are handled by the code that already does that — never by the
  browser. It also keeps a future `connect-src 'self'` honest, which a direct browser-to-Azure socket
  or a WebRTC path would not.

  A SEPARATE VOCABULARY FROM `clientMessageSchema` above, deliberately. That one carries commands and
  presence for a project everybody shares; this carries audio for one person's conversation. Folding
  them together would put an optional, metered, per-user feature inside the frame union that live
  editing depends on, and every reader of either would have to know about both.
*/
export const voiceClientMessageSchema = z.discriminatedUnion('t', [
    /**
     * A chunk of microphone audio, base64 PCM16 at `REALTIME_SAMPLE_RATE`.
     *
     * Bounded, because a websocket frame is not a request and nothing else here would stop one. 64 KiB
     * of base64 is about a second of audio at this rate — an order of magnitude more than a frame
     * should carry, which is the right place for a limit that exists to refuse the absurd rather than
     * to tune the ordinary.
     */
    z.object({ t: z.literal('audio'), b64: z.string().min(1).max(65_536) }),
    /** Stop listening but keep the session open — the mute, which must reach the server to mean anything. */
    z.object({ t: z.literal('mute'), on: z.boolean() }),
    /** Done. Closes the session politely rather than by dropping the socket. */
    z.object({ t: z.literal('bye') }),
]);
/** Why a spoken session ended, in words a client can turn into a sentence. */
export const VOICE_END_REASONS = [
    'bye',
    'ceiling',
    'idle',
    'upstream-failed',
    'evicted',
];
export const voiceEndReasonSchema = z.enum(VOICE_END_REASONS);
//# sourceMappingURL=index.js.map